import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'

// Mirrors api/src/chat/providers/registry.ts. The model CATALOG is fetched from
// /settings/ai/me rather than hardcoded here, so only the union is mirrored.
export type Provider = 'anthropic' | 'openai' | 'google' | 'xai'

export interface AiSettingsView {
  active_provider: Provider
  model: string
  providers: Record<Provider, { hasKey: boolean }>
}

export interface SaveAiInput {
  active_provider?: Provider
  model?: string
  keys?: Partial<Record<Provider, string>>
}

export interface CatalogModel {
  id: string
  label: string
}

export interface MyAiView {
  org_default: { provider: Provider; model: string }
  selection: { provider: Provider; model: string } | null
  allowed: { provider: Provider; model: string; label: string }[]
  catalog: { provider: Provider; label: string; default_model: string; models: CatalogModel[]; source?: 'live' | 'fallback' | 'unconfigured' }[]
}

export type AiAccess = Partial<Record<Provider, '*' | string[]>>

export interface AiAccessRow {
  user_id: string
  email: string
  role: string
  access: AiAccess | null
}

export interface OrgUser {
  id: string
  email: string
  role: string
}

const AI_SETTINGS_KEY = ['ai-settings'] as const
const MY_AI_KEY = ['ai-settings', 'me'] as const
const AI_ACCESS_KEY = ['ai-settings', 'access'] as const

export function useAiSettings() {
  return useQuery({
    queryKey: AI_SETTINGS_KEY,
    queryFn: () => api.get<AiSettingsView>('/settings/ai'),
  })
}

export function useSaveAiSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveAiInput) => api.put<AiSettingsView>('/settings/ai', input),
    // Keys/default changes reshape every user's allowed set, so refresh /me too.
    onSuccess: () => qc.invalidateQueries({ queryKey: AI_SETTINGS_KEY }),
  })
}

/** The caller's own menu + selection. Available to every authed user. */
export function useMyAi() {
  return useQuery({
    queryKey: MY_AI_KEY,
    queryFn: () => api.get<MyAiView>('/settings/ai/me'),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000,
  })
}

export function useSaveMyAi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (selection: { provider: Provider; model: string } | null) =>
      api.put<MyAiView>('/settings/ai/me', { selection }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MY_AI_KEY }),
  })
}

/** Admin: per-user grants. */
export function useAiAccess() {
  return useQuery({
    queryKey: AI_ACCESS_KEY,
    queryFn: () => api.get<AiAccessRow[]>('/settings/ai/access'),
  })
}

export function useSaveAiAccess() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, access }: { userId: string; access: AiAccess | null }) =>
      api.put<AiAccessRow[]>(`/settings/ai/access/${userId}`, { access }),
    onSuccess: () => qc.invalidateQueries({ queryKey: AI_ACCESS_KEY }),
  })
}

export function useOrgUsers() {
  return useQuery({
    queryKey: ['org-users'],
    queryFn: () => api.get<OrgUser[]>('/org/users'),
  })
}
